import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SiteSearch from '../../components/feature/SiteSearch';
import BrainChemistryFortune from '../../components/feature/BrainChemistryFortune';
import PharmacyCalculator from '../../components/feature/PharmacyCalculator';

export default function HomePage() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNavbarScrolled, setIsNavbarScrolled] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [playingEpisode, setPlayingEpisode] = useState<number | null>(null);

  // 統合されたサービス一覧
  const allServices = [
    // 患者さん向けサービス
    {
      title: 'セルフケア支援',
      description: 'サルコペニア予防、フレイル対策、栄養指導',
      icon: 'ri-run-line',
      color: 'bg-green-50 text-green-600',
      link: '/health-support',
      category: 'patient'
    },
    {
      title: 'かかりつけ薬剤師',
      description: '継続的な服薬管理と健康サポート',
      icon: 'ri-user-heart-line',
      color: 'bg-teal-50 text-teal-600',
      link: '/family-pharmacist',
      category: 'patient'
    },
    {
      title: 'オンライン服薬指導',
      description: 'ビデオ通話による薬剤師の服薬指導サービス',
      icon: 'ri-video-line',
      color: 'bg-blue-50 text-blue-600',
      link: '/online-medication-guidance',
      category: 'patient'
    },
    // 薬剤師向けサービス
    {
      title: '専門薬局情報',
      description: '専門医療機関連携薬局、地域連携薬局の運営',
      icon: 'ri-hospital-line',
      color: 'bg-red-50 text-red-600',
      link: '/specialty-pharmacy',
      category: 'pharmacist'
    },
    {
      title: '無菌調剤',
      description: 'クリーンベンチ導入、無菌操作手順',
      icon: 'ri-test-tube-line',
      color: 'bg-cyan-50 text-cyan-600',
      link: '/sterile-preparation',
      category: 'pharmacist'
    },
    {
      title: '在宅医療',
      description: '多職種連携、在宅調剤のポイント',
      icon: 'ri-home-heart-line',
      color: 'bg-pink-50 text-pink-600',
      link: '/home-care',
      category: 'pharmacist'
    },
    {
      title: 'DX・デジタル化',
      description: 'オンライン服薬指導、電子処方箋対応',
      icon: 'ri-smartphone-line',
      color: 'bg-indigo-50 text-indigo-600',
      link: '/dx',
      category: 'pharmacist'
    },
    {
      title: '添付文書検索',
      description: 'PMDA添付文書データベース検索',
      icon: 'ri-file-search-line',
      color: 'bg-blue-50 text-blue-600',
      link: '/package-insert-search',
      category: 'pharmacist'
    },
    {
      title: '薬価サーチ',
      description: '薬価基準収載品目検索',
      icon: 'ri-money-yen-circle-line',
      color: 'bg-green-50 text-green-600',
      link: 'https://yakka-search.com/',
      category: 'pharmacist'
    },
    {
      title: '転職・派遣情報',
      description: '薬剤師専門の転職・派遣支援、求人情報',
      icon: 'ri-briefcase-line',
      color: 'bg-orange-50 text-orange-600',
      link: '/career',
      category: 'pharmacist'
    }
  ];

  // 全ビースト君ブログ記事データ（日付順で自動ソート）
  const allBlogPosts = [
    {
      title: '🚨 【命を守る目】薬剤師があなたのお薬手帳を見て「即、疑義照会！」する危険なサイン3選',
      path: '/beast-blog/medication-safety-check',
      date: '2025.11.16',
      sortDate: new Date('2025-11-16')
    },
    {
      title: '薬剤師が教える！災害時のお薬管理マニュアル',
      path: '/beast-blog/disaster-medication-management',
      date: '2025.11.12',
      sortDate: new Date('2025-11-12')
    },
    {
      title: '患者のための薬局ビジョン',
      path: '/beast-blog/patient-pharmacy-vision',
      date: '2025.11.08',
      sortDate: new Date('2025-11-08')
    },
    {
      title: '第８次医療計画の５疾病６事業について',
      path: '/beast-blog/medical-plan-5diseases-6projects',
      date: '2025.11.05',
      sortDate: new Date('2025-11-05')
    },
    {
      title: 'インフルエンザ予防に薬局でできること｜薬剤師監修',
      path: '/beast-blog/influenza-prevention-pharmacy',
      date: '2025.10.30',
      sortDate: new Date('2025-10-30')
    },
    {
      title: '在宅医療における薬剤師の役割と支援内容をわかりやすく紹介',
      path: '/beast-blog/home-medical-care-pharmacist',
      date: '2025.10.25',
      sortDate: new Date('2025-10-25')
    },
    {
      title: '秋の花粉症対策｜春との違いとおすすめの薬・ケア方法',
      path: '/beast-blog/autumn-hay-fever-guide',
      date: '2025.10.20',
      sortDate: new Date('2025-10-20')
    },
    {
      title: 'かかりつけ薬剤師とは？メリットと登録方法を徹底解説',
      path: '/beast-blog/family-pharmacist-guide',
      date: '2025.10.15',
      sortDate: new Date('2025-10-15')
    },
    {
      title: 'サプリメントと薬の飲み合わせは大丈夫？注意すべき組み合わせ一覧',
      path: '/beast-blog/supplement-drug-interactions',
      date: '2025.10.08',
      sortDate: new Date('2025-10-08')
    },
    {
      title: '薬の飲み合わせで注意すべき食べ物10選【薬剤師監修】',
      path: '/beast-blog/drug-food-interactions',
      date: '2025.10.07',
      sortDate: new Date('2025-10-07')
    },
    {
      title: '薬が効かないときの原因5選｜飲み方・生活習慣・相互作用をチェック',
      path: '/beast-blog/medicine-not-working',
      date: '2025.10.06',
      sortDate: new Date('2025-10-06')
    },
    {
      title: 'ジェネリック医薬品とは？違い・メリット・デメリットを薬剤師が解説',
      path: '/beast-blog/generic-medicine',
      date: '2025.10.05',
      sortDate: new Date('2025-10-05')
    },
    {
      title: '薬剤師目線での多職種連携について',
      path: '/beast-blog/multidisciplinary-collaboration',
      date: '2025.10.04',
      sortDate: new Date('2025-10-04')
    },
    {
      title: '冬のメンタルヘルス対策｜薬剤師ができるサポート',
      path: '/beast-blog/winter-mental-health',
      date: '2025.10.03',
      sortDate: new Date('2025-10-03')
    }
  ];

  // 日付順でソートして最新3記事を取得（オレンジ背景セクション用）
  const latestBlogPosts = [...allBlogPosts]
    .sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime())
    .slice(0, 3);

  // 最新記事セクション用（下部の白背景）も同様に最新3記事を取得
  const latestNewsItems = [...allBlogPosts]
    .sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime())
    .slice(0, 3);

  const consultationExamples = [
    {
      category: '健康診断結果について',
      examples: [
        '血糖値が高めと言われたが、どう改善すればよいか',
        'コレステロール値の意味と対策方法を知りたい',
        '血圧が気になるが薬を飲むべきか相談したい',
        '肝機能の数値について詳しく説明してほしい'
      ]
    },
    {
      category: '薬の飲み合わせ・副作用',
      examples: [
        '複数の薬を飲んでいるが相互作用が心配',
        '薬を飲み始めてから体調が悪い気がする',
        'サプリメントと処方薬の飲み合わせについて',
        '薬の副作用かどうか判断してほしい'
      ]
    },
    {
      category: 'OTC医薬品の選び方',
      examples: [
        '風邪薬の種類が多くてどれを選べばよいかわからない',
        '胃薬の効果的な選び方を教えてほしい',
        '湿布薬の使い分けについて知りたい',
        '目薬の選び方と正しい使用方法について'
      ]
    },
    {
      category: '生活習慣・健康管理',
      examples: [
        '糖尿病予防のための生活習慣について',
        '高血圧の改善方法を具体的に知りたい',
        '骨粗鬆症予防のためにできることは何か',
        '認知症予防に効果的な方法について'
      ]
    }
  ];

  const handlePlayAudio = (episodeNumber: number, audioUrl: string) => {
    // 既に再生中の音声があれば停止
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }

    // 同じエピソードをクリックした場合は停止
    if (playingEpisode === episodeNumber) {
      setCurrentAudio(null);
      setPlayingEpisode(null);
      return;
    }

    // 新しい音声を再生
    const audio = new Audio(audioUrl);
    audio.play();
    setCurrentAudio(audio);
    setPlayingEpisode(episodeNumber);

    // 音声終了時の処理
    audio.onended = () => {
      setCurrentAudio(null);
      setPlayingEpisode(null);
    };
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <div className="flex items-center">
                  <div className="w-10 h-10 mr-3 relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                      <i className="ri-heart-pulse-fill text-white text-lg"></i>
                    </div>
                  </div>
                  <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                    みんなの薬局～Pharmacare～
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <nav className="hidden md:flex space-x-8">
                  <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
                  <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
                  <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
                  <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
                </nav>
                
                {/* QR Code Section */}
                <a 
                  href="https://note.com/medihana_cook/n/n42e2352d7a31"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden lg:flex items-center bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center space-x-3">
                    <img 
                      src="https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/437847a161b7eb3dd99f5d539555cd86.png" 
                      alt="QRコード" 
                      className="w-12 h-12 bg-white p-1 rounded"
                    />
                    <div className="text-sm">
                      <div className="font-semibold">スマホでアクセス</div>
                      <div className="text-xs opacity-90">簡単アクセス</div>
                    </div>
                  </div>
                </a>
              </div>
              <button className="md:hidden p-2">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </header>

        {/* サイト内検索と感染症情報 */}
        <section className="bg-gradient-to-r from-teal-100 to-emerald-100 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* 左側: サイト内検索 + 計算ツール */}
              <div className="lg:col-span-2 flex flex-col gap-6">
                {/* サイト内検索 */}
                <div>
                  <div className="text-left mb-4">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      <i className="ri-search-line mr-2"></i>
                      サイト内検索
                    </h2>
                    <p className="text-gray-600 text-sm">薬学情報、健康サポート、薬剤師向けリソースを素早く検索</p>
                  </div>
                  <SiteSearch />
                </div>

                {/* 薬学計算ツール */}
                <div>
                  <PharmacyCalculator />
                </div>
              </div>
              
              {/* 感染症情報 */}
              <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-teal-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-red-500 to-pink-500 rounded-lg">
                    <i className="ri-virus-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">感染症情報</h3>
                </div>
                <div className="space-y-3">
                  <a
                    href="https://www.mhlw.go.jp/stf/seisakunitsuite/bunya/kenkou_iryou/kenkou/kekkaku-kansenshou/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg hover:shadow-md transition-all duration-300 group cursor-pointer"
                  >
                    <span className="text-sm font-medium text-gray-700 group-hover:text-red-600 transition-colors whitespace-nowrap">厚労省 感染症情報</span>
                    <i className="ri-external-link-line text-gray-400 group-hover:text-red-600 transition-colors"></i>
                  </a>
                  <a
                    href="https://www.niid.go.jp/niid/ja/flu-map.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg hover:shadow-md transition-all duration-300 group cursor-pointer"
                  >
                    <span className="text-sm font-medium text-gray-700 group-hover:text-orange-600 transition-colors whitespace-nowrap">インフルエンザ流行マップ</span>
                    <i className="ri-external-link-line text-gray-400 group-hover:text-orange-600 transition-colors"></i>
                  </a>
                  <a
                    href="https://www.mhlw.go.jp/stf/seisakunitsuite/bunya/0000164708_00001.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg hover:shadow-md transition-all duration-300 group cursor-pointer"
                  >
                    <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors whitespace-nowrap">新型コロナ情報</span>
                    <i className="ri-external-link-line text-gray-400 group-hover:text-blue-600 transition-colors"></i>
                  </a>
                  <a
                    href="https://www.niid.go.jp/niid/ja/idwr.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg hover:shadow-md transition-all duration-300 group cursor-pointer"
                  >
                    <span className="text-sm font-medium text-gray-700 group-hover:text-purple-600 transition-colors whitespace-nowrap">感染症週報（IDWR）</span>
                    <i className="ri-external-link-line text-gray-400 group-hover:text-purple-600 transition-colors"></i>
                  </a>
                </div>
              </div>

              {/* 検査値基準値 */}
              <Link
                to="/lab-values"
                className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-teal-100 hover:shadow-xl hover:border-teal-300 transition-all duration-300 group cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-flask-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 group-hover:text-teal-600 transition-colors">検査値基準値</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  血液検査・肝機能・腎機能など、主要な検査項目の基準値を素早く確認できます。
                </p>
                <div className="mt-4 flex items-center text-teal-600 text-sm font-medium group-hover:translate-x-2 transition-transform duration-300">
                  <span className="whitespace-nowrap">基準値を確認する</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Hero Section */}
        <section className="relative py-20 min-h-[90vh] flex items-center overflow-hidden">
          {/* 背景グラデーション - 明るく鮮やかなティール/エメラルド系 */}
          <div className="absolute inset-0 bg-gradient-to-br from-teal-400 via-emerald-300 to-cyan-400"></div>
          
          {/* グリッドパターンオーバーレイ */}
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
          
          {/* 斜めの光ライン */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -left-20 w-[600px] h-[3px] bg-gradient-to-r from-transparent via-white/40 to-transparent transform rotate-[35deg]"></div>
            <div className="absolute top-1/4 -right-20 w-[500px] h-[2px] bg-gradient-to-r from-transparent via-white/30 to-transparent transform -rotate-[25deg]"></div>
            <div className="absolute bottom-1/3 -left-10 w-[400px] h-[2px] bg-gradient-to-r from-transparent via-emerald-100/50 to-transparent transform rotate-[15deg]"></div>
          </div>
          
          {/* 装飾的な光の球体 */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-[10%] w-72 h-72 bg-white/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '10s' }}></div>
            <div className="absolute bottom-20 right-[10%] w-64 h-64 bg-emerald-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '14s' }}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cyan-200/20 rounded-full blur-3xl"></div>
            <div className="absolute top-[20%] right-[30%] w-40 h-40 bg-white/15 rounded-full blur-2xl animate-pulse" style={{ animationDuration: '16s' }}></div>
          </div>
          
          {/* 下部を少し深みのある色に */}
          <div className="absolute inset-0 bg-gradient-to-t from-teal-600/20 via-transparent to-emerald-300/10"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* メインタイトル */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight drop-shadow-lg">
                薬局マネジメントフレームワーク
              </h1>
              <p className="text-xl md:text-2xl text-white/90 font-medium drop-shadow">
                Pharmacy Management Framework - 4-Element Integrated Model
              </p>
            </div>

            {/* 4要素統合モデル */}
            <div className="relative max-w-6xl mx-auto">
              {/* 中央の相互作用エリア */}
              <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-4">
                
                {/* 左側: 理論 & 能力 */}
                <div className="flex flex-col gap-6 lg:w-1/4">
                  {/* 理論 (Theory) */}
                  <a href="https://thepharmabeast.com/theory" target="_blank" rel="noopener noreferrer" className="block bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-6 shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <i className="ri-book-open-line text-2xl text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">理論（Theory）</h3>
                        <p className="text-amber-100 text-sm">論理的思考と体系的アプローチ</p>
                      </div>
                    </div>
                    <ul className="space-y-2 text-white/90 text-sm">
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-amber-200"></i>
                        エビデンスに基づく意思決定
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-amber-200"></i>
                        業務プロセスの標準化
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-amber-200"></i>
                        PDCAサイクルの実践
                      </li>
                    </ul>
                  </a>

                  {/* 能力 (Capability) */}
                  <div className="bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl p-6 shadow-2xl transform hover:scale-105 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <i className="ri-user-star-line text-2xl text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">能力（Capability）</h3>
                        <p className="text-cyan-100 text-sm">個人の力の最大化</p>
                      </div>
                    </div>
                    <ul className="space-y-2 text-white/90 text-sm">
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-cyan-200"></i>
                        スキル評価と育成
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-cyan-200"></i>
                        専門性の開発
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-cyan-200"></i>
                        チーム力の向上
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 中央: 4要素の相互作用 */}
                <div className="lg:w-2/4 flex flex-col items-center justify-center py-8">
                  <div className="relative w-72 h-72 md:w-80 md:h-80">
                    {/* 中央の円 */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-40 h-40 bg-white/25 backdrop-blur-sm rounded-full border-2 border-white/50 flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-white font-bold text-lg">4要素の</p>
                          <p className="text-white/80 font-bold text-lg">相互作用</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* 回転する矢印アニメーション */}
                    <div className="absolute inset-0 animate-spin" style={{ animationDuration: '20s' }}>
                      <svg className="w-full h-full" viewBox="0 0 200 200">
                        <defs>
                          <linearGradient id="arrowGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#22d3ee" />
                            <stop offset="100%" stopColor="#14b8a6" />
                          </linearGradient>
                        </defs>
                        <path d="M 100 20 A 80 80 0 0 1 180 100" fill="none" stroke="url(#arrowGradient1)" strokeWidth="4" strokeLinecap="round" />
                        <path d="M 180 100 A 80 80 0 0 1 100 180" fill="none" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
                        <path d="M 100 180 A 80 80 0 0 1 20 100" fill="none" stroke="#22d3ee" strokeWidth="4" strokeLinecap="round" />
                        <path d="M 20 100 A 80 80 0 0 1 100 20" fill="none" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
                      </svg>
                    </div>

                    {/* 4つの要素ラベル */}
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                      理論が方向性を示し
                    </div>
                    <div className="absolute top-1/2 -right-4 transform -translate-y-1/2 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                      動機がエネルギーを生み
                    </div>
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                      時間が成果を形にする
                    </div>
                    <div className="absolute top-1/2 -left-4 transform -translate-y-1/2 bg-cyan-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                      能力が実行力となり
                    </div>
                  </div>

                  {/* 統合的マネジメント */}
                  <div className="mt-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl p-4 border border-white/20 shadow-xl max-w-md">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <i className="ri-scales-3-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-white font-bold">統合的マネジメント</h4>
                        <p className="text-cyan-100 text-sm">4要素のバランスが薬局の持続的成長と質の高い医療サービスを実現</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 右側: 動機 & 時間 */}
                <div className="flex flex-col gap-6 lg:w-1/4">
                  {/* 動機 (Motivation) */}
                  <a href="https://thepharmabeast.com/motivation" target="_blank" rel="noopener noreferrer" className="block bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl p-6 shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <i className="ri-heart-pulse-line text-2xl text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">動機（Motivation）</h3>
                        <p className="text-orange-100 text-sm">職員のモチベーション向上</p>
                      </div>
                    </div>
                    <ul className="space-y-2 text-white/90 text-sm">
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-orange-200"></i>
                        目標設定と承認
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-orange-200"></i>
                        キャリア支援
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-orange-200"></i>
                        働きがいのある環境づくり
                      </li>
                    </ul>
                  </a>

                  {/* 時間 (Time) */}
                  <div className="bg-gradient-to-br from-teal-400 to-emerald-500 rounded-2xl p-6 shadow-2xl transform hover:scale-105 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <i className="ri-time-line text-2xl text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">時間（Time）</h3>
                        <p className="text-teal-100 text-sm">価値創出の最適化</p>
                      </div>
                    </div>
                    <ul className="space-y-2 text-white/90 text-sm">
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-teal-200"></i>
                        業務効率化
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-teal-200"></i>
                        優先順位の明確化
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-teal-200"></i>
                        患者価値の最大化
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 実践サイクル */}
              <div className="mt-12 bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/40">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="text-white font-bold text-lg">実践サイクル</div>
                  <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
                    <div className="flex items-center gap-2 bg-amber-500/80 px-4 py-2 rounded-full">
                      <i className="ri-lightbulb-line text-white"></i>
                      <span className="text-white text-sm font-medium">理論 (Theory) 計画 (Plan)</span>
                    </div>
                    <i className="ri-arrow-right-line text-white/60 hidden md:block"></i>
                    <div className="flex items-center gap-2 bg-orange-500/80 px-4 py-2 rounded-full">
                      <i className="ri-rocket-line text-white"></i>
                      <span className="text-white text-sm font-medium">動機 (Motivation) 推進 (Drive)</span>
                    </div>
                    <i className="ri-arrow-right-line text-white/60 hidden md:block"></i>
                    <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                      <i className="ri-refresh-line text-white"></i>
                      <span className="text-white text-sm font-medium">改善へ</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* キャッチコピー */}
              <div className="mt-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow">
                  薬局は、<span className="text-teal-50">理論</span>で回り、<span className="text-amber-200">人</span>で伸びる。
                </h2>
                <p className="text-xl text-white/90 mb-6 drop-shadow">
                  理論 × チームビルディング × 職員の力で
                </p>
                <p className="text-2xl font-bold text-white drop-shadow">
                  "偶然うまくいく薬局"から、<span className="text-amber-300">"再現できる薬局"</span>へ
                </p>
              </div>

              {/* CTA */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="/services" 
                  className="px-10 py-4 bg-white text-blue-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap text-lg cursor-pointer"
                >
                  サービスを見る
                </a>
                <a 
                  href="/beast-blog" 
                  className="px-10 py-4 bg-transparent text-white rounded-lg font-semibold hover:bg-white/10 transition-colors border-2 border-white whitespace-nowrap text-lg cursor-pointer"
                >
                  ブログ記事を読む
                </a>
                <a 
                  href="/entertainment" 
                  className="px-10 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap text-lg cursor-pointer"
                >
                  ポドキャストを聞く
                </a>
              </div>

              {/* サイトの位置づけ */}
              <p className="mt-8 text-center text-sm text-white/80 font-medium">
                薬剤師・調剤薬局関係者向けの専門メディア
              </p>
            </div>
          </div>
        </section>

      {/* ビースト君紹介セクション */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                みんなの薬局のマスコット
                <span className="text-teal-600">「ビースト君」</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                筋肉モリモリの薬剤師ビースト君が、皆さんの健康をサポートします！　　　　　　
                薬学知識と筋力を兼ね備えた頼れるパートナーです。
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">豊富な薬学知識でサポート</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">薬局運を理論で紐解きます</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">チームビルデングの重要性についても解説</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-80 h-80 bg-gradient-to-br from-teal-200 via-emerald-100 to-blue-200 rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                  <div className="w-72 h-72 bg-gradient-to-br from-teal-100 to-emerald-50 rounded-full flex items-center justify-center border-2 border-teal-200">
                    <img 
                      src="https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/abf95f083c0e416deb704963403c46c1.jpeg"
                      alt="ビースト君マスコート"
                      className="w-64 h-64 object-cover object-top rounded-full border-2 border-teal-300"
                    />
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-4 py-2 rounded-full text-sm font-bold transform rotate-12 shadow-lg border-2 border-white">
                  頼れる相棒！
                </div>
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-white px-6 py-3 rounded-full shadow-lg border-2 border-teal-500">
                  <span className="text-teal-600 font-bold text-lg">ビースト君</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* サービス紹介セクション */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-16">
            <span className="text-teal-600">PharmaBeast</span>が提供する価値
          </h2>
        </div>
      </section>

      {/* PharmaBeastのコンテンツ */}
      <section className="py-20 px-4 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">PharmaBeastのコンテンツ
</h2>
            <p className="text-gray-600 text-lg">PharmaBeastが提供する多彩なサービス</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* 1列目: 理論、チームビルディング */}
            <Link
              to="/theory"
              className="group bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-book-2-line text-3xl text-white"></i>
              </div>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-amber-600 transition-colors">
                  薬局運営の理論
                </h3>
                <i className="ri-arrow-right-s-line text-xl text-gray-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all"></i>
              </div>
              <p className="text-gray-600 mt-3">
                薬局経営の理論と実践、再現性のある組織づくりの方法論
              </p>
            </Link>

            <Link
              to="/motivation"
              className="group bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-fire-line text-3xl text-white"></i>
              </div>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors">
                  動機付け
                </h3>
                <i className="ri-arrow-right-s-line text-xl text-gray-400 group-hover:text-orange-600 group-hover:translate-x-1 transition-all"></i>
              </div>
              <p className="text-gray-600 mt-3">
                職員のモチベーション向上と働きがいのある環境づくり
              </p>
            </Link>

            <Link
              to="/team-building"
              className="group bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-team-line text-3xl text-white"></i>
              </div>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-rose-600 transition-colors">
                  チームビルディング
                </h3>
                <i className="ri-arrow-right-s-line text-xl text-gray-400 group-hover:text-rose-600 group-hover:translate-x-1 transition-all"></i>
              </div>
              <p className="text-gray-600 mt-3">
                職員の力を最大化し、強いチームを作るための実践的手法
              </p>
            </Link>

            {/* 2列目: かかりつけ薬剤師、在宅医療、無菌調剤 */}
            <Link
              to="/family-pharmacist"
              className="group bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-user-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                かかりつけ薬剤師
              </h3>
              <p className="text-gray-600">
                病院や調剤薬局の薬剤師との連携により、継続的な健康サポート
              </p>
            </Link>

            <Link
              to="/home-care"
              className="group bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-home-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                在宅医療
              </h3>
              <p className="text-gray-600">
                在宅医療の流れと薬剤師が行う具体的なサポートについて
              </p>
            </Link>

            <Link
              to="/sterile-preparation"
              className="group bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-test-tube-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                無菌調剤
              </h3>
              <p className="text-gray-600">
                無菌操作の仕組みと効果的な無菌調剤の方法について解説
              </p>
            </Link>

            {/* 3列目: 健康サポート、DX・デジタル化、転職・派遣情報 */}
            <Link
              to="/health-support"
              className="group bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                健康サポート
              </h3>
              <p className="text-gray-600">
                日々の健康管理から予防医療まで、トータルサポート
              </p>
            </Link>

            <Link
              to="/dx"
              className="group bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-smartphone-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                転職・派遣情報
              </h3>
              <p className="text-gray-600">
                薬剤師のキャリアアップと転職・派遣に関する情報
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* PharmaBeast｜脳内化学タイプ占い */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <BrainChemistryFortune />
        </div>
      </section>

      {/* ポドキャスト セクション */}
      <section className="py-20 px-4 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full mb-6 shadow-lg">
              <i className="ri-mic-line text-3xl text-white"></i>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              PharmaBeast Podcast
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              薬剤師のための学びの場。現場で役立つ知識や考え方を音声でお届けします。<br />
              通勤中や休憩時間に、気軽に学べるコンテンツです。
            </p>
          </div>

          {/* ポドキャストカード */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* エピソード1 */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-amber-100 group">
              <a 
                href="https://youtu.be/pq5mrty1YUU" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block relative h-48 bg-gradient-to-br from-amber-400 via-orange-400 to-red-400 flex items-center justify-center"
              >
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative z-10 text-center">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-4xl text-white"></i>
                  </div>
                  <span className="text-white/90 text-sm font-medium">Episode 01</span>
                </div>
              </a>
              <div className="p-6">
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                  <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">NEW</span>
                  <span>2025.01.24</span>
                  <span>・</span>
                  <span>19分</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                  2026年診療報酬改定（短冊）
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  1月23日通達の2026年診療報酬改定短冊についてのポイントを解説します。
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img 
                      src="https://public.readdy.ai/ai/img_res/03bb719f3879154e40789ba34c3e0323.jpg"
                      alt="ホスト"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="text-sm text-gray-600">ビースト君</span>
                  </div>
                  <a 
                    href="https://youtu.be/pq5mrty1YUU"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium text-sm cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-headphone-line"></i>
                    聴く
                  </a>
                </div>
              </div>
            </div>

            {/* エピソード2 */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-amber-100 group">
              <div className="relative h-48 bg-gradient-to-br from-teal-400 via-cyan-400 to-blue-400 flex items-center justify-center">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative z-10 text-center">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-4xl text-white"></i>
                  </div>
                  <span className="text-white/90 text-sm font-medium">Episode 02</span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                  <span>2025.01.08</span>
                  <span>・</span>
                  <span>28分</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                  チームビルディングの極意 〜職員の力を最大化する〜
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  薬局スタッフのモチベーションを高め、チーム全体のパフォーマンスを向上させる方法を紹介。
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20pharmacist%20portrait%20headshot%2C%20friendly%20smile%2C%20white%20coat%2C%20clean%20background%2C%20professional%20healthcare%20worker%2C%20warm%20lighting&width=40&height=40&seq=podcast-host-2&orientation=squarish"
                      alt="ホスト"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="text-sm text-gray-600">ビースト君</span>
                  </div>
                  <a
                    href="https://www.youtube.com/watch?v=YlY1CC8ab2E"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium text-sm cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-headphone-line"></i>
                    聴く
                  </a>
                </div>
              </div>
            </div>

            {/* エピソード3 */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-amber-100 group">
              <div className="relative h-48 bg-gradient-to-br from-violet-400 via-purple-400 to-fuchsia-400 flex items-center justify-center">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative z-10 text-center">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-4xl text-white"></i>
                  </div>
                  <span className="text-white/90 text-sm font-medium">Episode 03</span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                  <span>2025.01.01</span>
                  <span>・</span>
                  <span>35分</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                  キャリア選択の納得感 〜自分らしい薬剤師像を描く〜
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  薬剤師としてのキャリアパスを考える上で大切な視点と、納得のいく選択をするためのヒント。
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20pharmacist%20portrait%20headshot%2C%20friendly%20smile%2C%20white%20coat%2C%20clean%20background%2C%20professional%20healthcare%20worker%2C%20warm%20lighting&width=40&height=40&seq=podcast-host-3&orientation=squarish"
                      alt="ホスト"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="text-sm text-gray-600">ビースト君</span>
                  </div>
                  <button className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium text-sm cursor-pointer whitespace-nowrap">
                    <i className="ri-headphone-line"></i>
                    聴く
                  </button>
                  <button 
                    onClick={() => handlePlayAudio(3, '/audio/podcast-episode3.mp3')}
                    className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium text-sm cursor-pointer whitespace-nowrap"
                  >
                    <i className={playingEpisode === 3 ? "ri-pause-circle-line" : "ri-headphone-line"}></i>
                    {playingEpisode === 3 ? '停止' : '聴く'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 配信プラットフォーム */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-amber-100">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">配信プラットフォーム</h3>
              <p className="text-gray-600 text-sm">お好みのプラットフォームでお聴きいただけます</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#" className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-spotify-line text-xl text-green-600"></i>
                <span className="font-medium text-gray-700">Spotify</span>
              </a>
              <a href="#" className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-apple-line text-xl text-gray-800"></i>
                <span className="font-medium text-gray-700">Apple Podcasts</span>
              </a>
              <a href="#" className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-google-line text-xl text-red-500"></i>
                <span className="font-medium text-gray-700">Google Podcasts</span>
              </a>
              <a href="#" className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-rss-line text-xl text-orange-500"></i>
                <span className="font-medium text-gray-700">RSS Feed</span>
              </a>
            </div>
          </div>
        </div>
      </section>

        {/* 統合サービスセクション */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">その他一覧</h2>
              <p className="text-lg text-gray-600">患者さんと薬剤師の皆様をサポートする充実したサービス</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {allServices.map((service, index) => (
                service.link.startsWith('http') ? (
                  <a
                    key={index}
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:-translate-y-2 cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-lg ${service.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <i className={`${service.icon} text-xl`}></i>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        service.category === 'patient' 
                          ? 'bg-teal-100 text-teal-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {service.category === 'patient' ? '患者向け' : '薬剤師向け'}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
                  </a>
                ) : (
                  <Link
                    key={index}
                    to={service.link}
                    className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:-translate-y-2 cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-lg ${service.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <i className={`${service.icon} text-xl`}></i>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        service.category === 'patient' 
                          ? 'bg-teal-100 text-teal-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {service.category === 'patient' ? '患者向け' : '薬剤師向け'}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
                  </Link>
                )
              ))}
            </div>
          </div>
        </section>

        {/* Quick Access Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">クイックアクセス</h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-red-50 text-red-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-emergency-line text-xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">緊急時対応</h3>
                <p className="text-gray-600 mb-4">災害時の薬剤師活動、モバイルファーマシー、DMAT連携</p>
                <Link to="/emergency" className="text-teal-600 font-medium hover:text-teal-700 cursor-pointer">詳細を見る →</Link>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-information-line text-xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">公的機関リンク</h3>
                <p className="text-gray-600 mb-4">厚生労働省、PMDA、日本薬剤師会の最新情報</p>
                <Link to="/official-links" className="text-teal-600 font-medium hover:text-teal-700 cursor-pointer">詳細を見る →</Link>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-green-50 text-green-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-briefcase-line text-xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">医療・介護用品サイト</h3>
                <p className="text-gray-600 mb-4">医療機器、医療用品、介護用品の購入サイト情報</p>
                <Link to="/medical-purchase" className="text-teal-600 font-medium hover:text-teal-700 cursor-pointer">詳細を見る →</Link>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-truck-line text-xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">医薬品卸サイト</h3>
                <p className="text-gray-600 mb-4">医薬品・医療機器の専門卸売業者情報</p>
                <Link to="/pharmaceutical-wholesale" className="text-teal-600 font-medium hover:text-teal-700 cursor-pointer">詳細を見る →</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Career Information Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">薬剤師転職・派遣情報</h2>
              <p className="text-lg text-gray-600">キャリアアップを目指す薬剤師の皆様をサポートします</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl shadow-lg p-8 border border-orange-100">
                <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-search-line text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">求人検索</h3>
                <p className="text-gray-600 mb-6">全国の薬剤師求人・派遣情報を検索。病院、調剤薬局、ドラッグストア、企業など幅広い職種に対応</p>
                <Link 
                  to="/career"
                  className="inline-flex items-center text-orange-600 font-medium hover:text-orange-700 cursor-pointer"
                >
                  求人を探す
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg p-8 border border-blue-100">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-user-star-line text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">キャリア相談</h3>
                <p className="text-gray-600 mb-6">専門コンサルタントによる個別相談。転職・派遣のタイミングや条件交渉まで丁寧にサポート</p>
                <Link 
                  to="/career"
                  className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 cursor-pointer"
                >
                  相談する
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl shadow-lg p-8 border border-green-100">
                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-graduation-cap-line text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">スキルアップ</h3>
                <p className="text-gray-600 mb-6">認定薬剤師資格取得支援、研修情報、専門分野への転職・派遣サポート</p>
                <Link 
                  to="/career"
                  className="inline-flex items-center text-green-600 font-medium hover:text-green-700 cursor-pointer"
                >
                  詳細を見る
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* News Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">最新情報</h2>
              <Link to="/beast-blog" className="text-teal-600 font-medium hover:text-teal-700 cursor-pointer">すべて見る →</Link>
            </div>
            <div className="space-y-4">
              {latestNewsItems.map((post, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm text-gray-500">{post.date}</span>
                        <span className="bg-teal-100 text-teal-800 text-xs px-2 py-1 rounded-full">ビースト君ブログ</span>
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">NEW</span>
                      </div>
                      <Link to={post.path} className="text-lg font-semibold text-gray-900 hover:text-teal-600 cursor-pointer">{post.title}</Link>
                    </div>
                    <i className="ri-arrow-right-line text-gray-400 ml-4"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 mr-2 relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                      <i className="ri-heart-pulse-fill text-white text-sm"></i>
                    </div>
                  </div>
                  <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                    みんなの薬局～Pharmacare～
                  </div>
                </div>
                <p className="text-gray-400">
                  患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">患者さん向け</h3>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/health-consultation" className="hover:text-white cursor-pointer">健康相談</Link></li>
                  <li><Link to="/self-care" className="hover:text-white cursor-pointer">セルフケア</Link></li>
                  <li><Link to="/kampo" className="hover:text-white cursor-pointer">漢方相談</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                  <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                  <li><Link to="/online-guidance" className="hover:text-white cursor-pointer">オンライン服薬指導</Link></li>
                  <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
                  <li><Link to="/career" className="hover:text-white cursor-pointer">転職・派遣情報</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/official-links" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                  <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                  <li><Link to="/career" className="hover:text-white cursor-pointer">転職・派遣情報</Link></li>
                  <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
                  <li><Link to="/contact" className="hover:text-white cursor-pointer">お問い合わせ</Link></li>
                  <li><Link to="/privacy-policy" className="hover:text-white cursor-pointer">プライバシーポリシー</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
